import { firebaseAuth } from '~/config/firebase.config';

/**
 * Studio création Unquiz — appel de la génération express IA.
 * La route vit sur la même Cloud Function que sendCouponEmail.
 */
const getFunctionsUrl = (route: 'generateQuiz' | 'cancelGenerateQuiz' = 'generateQuiz') => {
    if (process.env.NODE_ENV === 'development' || import.meta.env.DEV) {
        return `http://192.168.1.195:5001/${route}`;
    }
    return `https://sendcouponemail-5iiwpornyq-uc.a.run.app/${route}`;
};

/** Identifiant de la génération, impossible à deviner : lui seul permet de l'annuler. */
const newRequestId = () => {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    // Contexte non sécurisé (console locale en http) : randomUUID absent, getRandomValues toujours là.
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return `rq-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * En prod (Cloud Run), couper la connexion ne prévient pas le serveur : sans ce
 * message, une génération annulée continue jusqu'au bout et reste facturée.
 * text/plain + keepalive : part même si la page se ferme, sans préflight CORS.
 */
const cancelOnServer = (requestId: string) => {
    fetch(getFunctionsUrl('cancelGenerateQuiz'), {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ requestId }),
        keepalive: true,
    }).catch(() => {});
};

export type GenerateQuizParams = {
    theme: string;
    count: 10 | 15 | 20;
    difficulty: 'facile' | 'moyen' | 'difficile' | 'mix';
    lang?: QuizLang;
    /** Texte collé par l'animateur : le quiz est écrit à partir de lui. */
    source?: string;
    /** Établissement d'où part la demande (journal des créations IA, stats par établissement). */
    clientID?: string;
};

export type QuizLang = 'fr' | 'en' | 'de' | 'es' | 'it' | 'pt' | 'zh';
const QUIZ_LANGS: QuizLang[] = ['fr', 'en', 'de', 'es', 'it', 'pt', 'zh'];

/** Langue de génération depuis une langue i18n (« en-GB » → en) ; défaut fr. */
export const toQuizLang = (lang?: string): QuizLang => {
    const short = (lang ?? '').slice(0, 2).toLowerCase();
    return QUIZ_LANGS.find((l) => l === short) ?? 'fr';
};

export type GeneratedQuestion = {
    id: string;
    type: 'qcu' | 'vf';
    question: string;
    options: string[];
    correctAnswer: number;
    anecdote?: string;
    duration?: number;
};

export type GeneratedQuiz = {
    title: string;
    description?: string;
    questions: GeneratedQuestion[];
};

export type QuizGenerationCode = 'AI_NOT_CONFIGURED' | 'AUTH' | 'NO_SITE' | 'GENERATION_FAILED' | 'UNKNOWN_TOPIC';

export class QuizGenerationError extends Error {
    constructor(
        public code: QuizGenerationCode,
        public detail?: string
    ) {
        super(code);
    }
}

/** Progression réelle : `done` questions entièrement écrites sur `total`. */
export type QuizGenerationProgress = (done: number, total: number) => void;

/** Lecture du flux NDJSON d'une génération (voir QuizStudioAPI.generateQuiz). */
async function readGeneration(
    idToken: string,
    params: GenerateQuizParams & { requestId: string },
    onProgress?: QuizGenerationProgress,
    signal?: AbortSignal
): Promise<GeneratedQuiz> {
    const response = await fetch(getFunctionsUrl(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ ...params, lang: params.lang ?? 'fr', stream: true }),
        signal,
    });

    if (response.status === 503) throw new QuizGenerationError('AI_NOT_CONFIGURED');
    if (response.status === 401) throw new QuizGenerationError('AUTH');
    // Compte sans établissement : réessayer ne changerait rien.
    if (response.status === 403) throw new QuizGenerationError('NO_SITE');
    if (!response.ok || !response.body) throw new QuizGenerationError('GENERATION_FAILED');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const handle = (line: string): GeneratedQuiz | null => {
        if (!line.trim()) return null;
        const msg = JSON.parse(line);
        if (msg.type === 'progress') onProgress?.(msg.done, msg.total);
        else if (msg.type === 'quiz') return msg.quiz as GeneratedQuiz;
        else if (msg.type === 'error') {
            const code: QuizGenerationCode = ['AI_NOT_CONFIGURED', 'UNKNOWN_TOPIC'].includes(msg.error)
                ? msg.error
                : 'GENERATION_FAILED';
            throw new QuizGenerationError(code, msg.message);
        }
        return null;
    };
    for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
            const quiz = handle(line);
            if (quiz) return quiz;
        }
        if (done) {
            const quiz = handle(buffer);
            if (quiz) return quiz;
            throw new QuizGenerationError('GENERATION_FAILED');
        }
    }
}

export const QuizStudioAPI = {
    /**
     * Le serveur répond en NDJSON (stream: true) : une ligne par question écrite,
     * puis le quiz ou l'erreur. `onProgress` suit la vraie avancée de l'IA.
     */
    async generateQuiz(
        params: GenerateQuizParams,
        onProgress?: QuizGenerationProgress,
        /** Annuler : coupe la connexion ET prévient le serveur, qui arrête la génération. */
        signal?: AbortSignal
    ): Promise<GeneratedQuiz> {
        const user = firebaseAuth.currentUser;
        if (!user) throw new QuizGenerationError('AUTH');
        const idToken = await user.getIdToken();

        // Annuler pendant la génération → message « stop » au serveur (une seule fois,
        // et plus rien une fois la réponse terminée).
        const requestId = newRequestId();
        const onAbort = () => cancelOnServer(requestId);
        signal?.addEventListener('abort', onAbort, { once: true });
        try {
            return await readGeneration(idToken, { ...params, requestId }, onProgress, signal);
        } finally {
            signal?.removeEventListener('abort', onAbort);
        }
    },
};
